import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
  BarChart3Icon,
  BuildingIcon,
  FileTextIcon,
  EyeIcon,
  SettingsIcon,
  MessageCircleIcon,
  ChevronRight,
  HomeIcon,
  User2Icon,
  MailIcon,
  InfoIcon,
  MailSearchIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { SiteBuildMetadata } from "@/components/site-build-metadata";
import { ProductEmailInvitation } from "@/components/product-email-invitation";
import type { LucideIcon } from "lucide-react";

type SidebarChildItem = {
  to: string;
  label: string;
  icon: LucideIcon;
};

type SidebarNavItem = {
  label: string;
  icon: LucideIcon;
  to?: string;
  children?: SidebarChildItem[];
};

const siteAdminNavigationItems: SidebarNavItem[] = [
  {
    label: "Dashboard",
    icon: BarChart3Icon,
    to: "/site-admin/dashboard",
  },
  {
    label: "Management",
    icon: BuildingIcon,
    children: [
      {
        to: "/site-admin/organizations",
        icon: HomeIcon,
        label: "Organizations",
      },
      {
        to: "/site-admin/users",
        icon: User2Icon,
        label: "Users",
      },
      {
        to: "/site-admin/invitation-diagnostics",
        icon: MailSearchIcon,
        label: "Invitation Diagnostics",
      },
    ],
  },
  {
    label: "System",
    icon: MessageCircleIcon,
    children: [
      {
        to: "/site-admin/contact-submissions",
        icon: MailIcon,
        label: "Contact Submissions",
      },
      {
        to: "/site-admin/impersonation",
        icon: EyeIcon,
        label: "Impersonation Logs",
      },
      {
        to: "/site-admin/audit-logs",
        icon: FileTextIcon,
        label: "Audit Logs",
      },
    ],
  },
  {
    label: "Settings",
    icon: SettingsIcon,
    to: "/site-admin/settings",
  },
];

const SiteAdminPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isMobile = useIsMobile();
  const isCollapsedDesktop = !isSidebarOpen && !isMobile;

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="flex min-w-0 flex-1 flex-col min-h-full w-full">
        <div className="flex min-w-0 flex-row flex-1">
          <Sidebar
            className={`w-64 border-r-muted border-r flex-none bg-background z-10 md:top-16 md:pb-16`}
            collapsible="icon"
          >
            <SidebarHeader className="bg-background">
              <div className="p-4 group-data-[collapsible=icon]:px-2">
                <h2 className="text-lg font-semibold text-foreground group-data-[collapsible=icon]:hidden">
                  Site Administration
                </h2>
                <p className="text-sm text-muted-foreground group-data-[collapsible=icon]:hidden">
                  Platform Management
                </p>
                <div className="hidden group-data-[collapsible=icon]:flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                  <SettingsIcon className="size-4" />
                </div>
              </div>
            </SidebarHeader>

            <SidebarContent className="bg-background">
              <SidebarGroup className="pt-0">
                <SidebarGroupContent>
                  <SidebarMenu>
                    {siteAdminNavigationItems.map((item) => {
                      const hasChildren = !!item.children?.length;

                      if (!hasChildren && item.to) {
                        return (
                          <SidebarMenuItem key={item.to}>
                            <SidebarMenuButton asChild tooltip={item.label}>
                              <Link
                                to={item.to}
                                activeProps={{ className: "bg-secondary text-secondary-foreground/100" }}
                                from={Route.fullPath}
                              >
                                <item.icon />
                                <span>{item.label}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      }

                      if (!item.children) {
                        return null;
                      }

                      const defaultOpen = true;

                      if (isCollapsedDesktop) {
                        return (
                          <SidebarMenuItem key={item.label}>
                            <Popover>
                              <PopoverTrigger asChild>
                                <SidebarMenuButton tooltip={item.label}>
                                  <item.icon />
                                  <span>{item.label}</span>
                                </SidebarMenuButton>
                              </PopoverTrigger>
                              <PopoverContent side="right" align="start" className="w-56 p-2">
                                <p className="px-2 pb-2 text-xs font-medium text-muted-foreground">{item.label}</p>
                                <div className="flex flex-col gap-1">
                                  {item.children.map((child) => (
                                    <Link
                                      key={child.to}
                                      to={child.to}
                                      from={Route.fullPath}
                                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground/80 hover:bg-accent hover:text-accent-foreground"
                                      activeProps={{ className: "bg-secondary text-secondary-foreground/100" }}
                                    >
                                      <child.icon className="size-4" />
                                      <span>{child.label}</span>
                                    </Link>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </SidebarMenuItem>
                        );
                      }

                      return (
                        <Collapsible key={item.label} defaultOpen={defaultOpen} className="group/collapsible">
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton tooltip={item.label}>
                                <item.icon />
                                <span>{item.label}</span>
                                <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                {item.children.map((child) => (
                                  <SidebarMenuSubItem key={child.to}>
                                    <SidebarMenuSubButton asChild>
                                      <Link
                                        to={child.to}
                                        from={Route.fullPath}
                                        activeProps={{ className: "bg-secondary text-secondary-foreground/100" }}
                                      >
                                        <child.icon />
                                        <span>{child.label}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>

            <SidebarFooter className="bg-background group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-1">
              <SidebarMenu className="hidden group-data-[collapsible=icon]:flex">
                <SidebarMenuItem>
                  <Popover>
                    <PopoverTrigger asChild>
                      <SidebarMenuButton tooltip="Build metadata" className="justify-center">
                        <InfoIcon className="size-4" />
                        <span className="sr-only">Build metadata</span>
                      </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="center" className="w-64">
                      <p className="text-xs text-muted-foreground">Site Administrator Access</p>
                      <SiteBuildMetadata compact className="mt-2 text-muted-foreground" />
                    </PopoverContent>
                  </Popover>
                </SidebarMenuItem>
              </SidebarMenu>
              <div className="p-4 border-t group-data-[collapsible=icon]:hidden">
                <p className="text-xs text-muted-foreground">Site Administrator Access</p>
                <SiteBuildMetadata compact className="mt-2 text-muted-foreground" />
              </div>
            </SidebarFooter>
            <SidebarRail />
          </Sidebar>

          <div className="flex min-w-0 flex-col flex-1 md:w-auto w-full">
            <ProductEmailInvitation />
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export const Route = createFileRoute("/_app/site-admin")({
  staticData: { sidebarLayout: true },
  component: SiteAdminPage,
});
